import dateHelper from '@/components/QuoteForm/dateHelper';
import { toast as toastify } from 'react-toastify';
import { toast } from 'react-toastify';
import closedDatesCalculator from './closedDatesCalculator';
import {
    normalizeDate,
    DAYS_OF_WEEK,
    formatForComparison,
    daysBetween,
    //debugDate,
} from './returnDateCalculator/dateUtils';
import {
    LOCALE_DATE_FORMAT,
    TIME_CONSTANTS,
    VEHICLE_TYPES,
} from './returnDateCalculator/constants';
import {
    DaysOfTheWeek,
    ReturnDateCalculatorProps,
} from './returnDateCalculator/types';
import {
    findClosurePeriod,
    handleEarlyCloseDay,
    handleConsecutiveClosurePeriod,
} from './returnDateCalculator/closureHandlers/closureHandlers';
import { toBusinessDate } from './date/businessDate';

export const daysOfWeek = DAYS_OF_WEEK;

// Strip zero-width chars that Sanity can embed in string fields.
// Without this, 'closed' !== 'closed' evaluates to true and a fully-closed
// day is misclassified as early-close, silencing the correct closure handler.
const stripZeroWidth = (s: string): string =>
    s.replace(/[\u200B-\u200D\uFEFF\u00A0\u2060]/g, '').trim();

const returnDateCalculator = ({
    returnDatePriceCalculator,
    vehicle,
    numRentalDays,
    closedDates,
    closedDatesDetails = [],
    setReturnDatePriceCalculator,
    mySetFieldValue,
    collectionDate,
}: ReturnDateCalculatorProps) => {
    // Sanitize closingTime on every CMS entry before any comparison.
    const sanitizedClosedDatesDetails = closedDatesDetails.map((d) => ({
        ...d,
        closingTime: stripZeroWidth(d.closingTime ?? ''),
    }));

    // Normalize closedDates from ISO to locale format for consistent comparisons
    // QuoteForm passes ISO strings; all comparisons in this function use locale format
    const normalizedClosedDates = closedDates.map((date) =>
        new Date(date).toLocaleDateString(LOCALE_DATE_FORMAT)
    );

    // Remove early close days from closedDates array
    const earlyCloseDates = sanitizedClosedDatesDetails
        .filter((d) => d.closingTime !== 'closed')
        .map((d) => new Date(d.date).toLocaleDateString(LOCALE_DATE_FORMAT));

    const filteredClosedDates = normalizedClosedDates.filter(
        (date) => !earlyCloseDates.includes(date)
    );

    // If there's no return date yet, don't proceed
    if (!returnDatePriceCalculator) return;

    // Only show toasts if we have complete data
    const retDateNormalized = normalizeDate(returnDatePriceCalculator);
    const colDateNormalized = normalizeDate(collectionDate);

    if (
        !collectionDate ||
        !returnDatePriceCalculator ||
        isNaN(colDateNormalized.date.getTime()) ||
        isNaN(retDateNormalized.date.getTime())
    ) {
        // We don't have collection date and time, AND return date and time just yet so just exit
        return;
    }

    // Use the following debug utils to check collection and return dates
    // debugDate('Return Date', returnDatePriceCalculator);
    // debugDate('Collection Date', collectionDate);

    // If the return date is Sunday, we must display
    // a message, then change the return time to be 5pm.
    const retDate = retDateNormalized.date;
    const returnDay = retDateNormalized.dayName as keyof DaysOfTheWeek;

    const colDate = colDateNormalized.date;
    const collectionDay = colDateNormalized.dayName as keyof DaysOfTheWeek;

    const hours = retDateNormalized.hours;
    const minutes = retDateNormalized.minutes;
    const retBusinessDate = toBusinessDate(retDate);
    const closurePeriodInfo = findClosurePeriod(
        retDate,
        sanitizedClosedDatesDetails
    );
    const currentEarlyCloseDate = closurePeriodInfo.earlyCloseDetails;
    let dateAlreadyProcessed = false;

    // Cross-day early close window check (must run before any other logic)
    for (const closedDetail of sanitizedClosedDatesDetails) {
        if (closedDetail.closingTime && closedDetail.reopenDate) {
            // Parse early close day, day before fully closed day, closing time, and re-open date
            const earlyCloseDate = new Date(closedDetail.date);
            const closedDayDate = new Date(closedDetail.date);
            const closingDay = new Date(closedDayDate);
            closingDay.setDate(closingDay.getDate() - 1);
            const timeParts = closedDetail.closingTime.split(':');
            const closeHour = parseInt(
                timeParts[0]?.replace(/[^\d]/g, '') || '18',
                10
            );
            const closeMinute = parseInt(
                timeParts[1]?.replace(/[^\d]/g, '') || '0',
                10
            );
            const earlyCloseDateTime = new Date(earlyCloseDate);
            earlyCloseDateTime.setHours(closeHour, closeMinute, 0, 0);

            const dayBeforeFullyClosedDateTime = new Date(closingDay);

            // 1-hour buffer before early close, or 1 hour before closing time on day before fully closed day
            const bufferDateTime = new Date(
                closedDetail.closingTime !== 'closed'
                    ? earlyCloseDateTime
                    : dayBeforeFullyClosedDateTime
            );
            bufferDateTime.setHours(closeHour - 1, closeMinute, 0, 0);

            const reopenDate = new Date(closedDetail.reopenDate);
            reopenDate.setHours(8, 0, 0, 0); // Set to 8:00am

            if (
                (closedDetail.closingTime !== 'closed' &&
                    retDate > bufferDateTime &&
                    retDate < reopenDate) ||
                (closedDetail.closingTime === 'closed' &&
                    (retDate > bufferDateTime ||
                        // Verify retDate is within THIS entry's consecutive closure
                        // window (not just any closed date before a future reopenDate).
                        // Walk forward from retDate to entry date: if every day is closed,
                        // retDate belongs to this window. This prevents a future bank
                        // holiday entry from firing for an earlier, unrelated closed day.
                        (() => {
                            if (!filteredClosedDates.includes(retDate.toLocaleDateString(LOCALE_DATE_FORMAT))) return false;
                            const [ey, em, ed] = closedDetail.date.split('-').map(Number);
                            const cur = new Date(retDate.getFullYear(), retDate.getMonth(), retDate.getDate(), 12);
                            const end = new Date(ey, em - 1, ed, 12);
                            if (cur > end) return false;
                            while (cur.getTime() < end.getTime()) {
                                cur.setDate(cur.getDate() + 1);
                                if (!filteredClosedDates.includes(cur.toLocaleDateString(LOCALE_DATE_FORMAT))) return false;
                            }
                            return true;
                        })()) &&
                    retDate < reopenDate)
            ) {
                // Walk the nominal reopenDate forward through any further closed days
                // (CMS entries still in filteredClosedDates or programmatic Sundays)
                // to find the true first open day before computing dayBeforeReopen.
                // This handles both CMS data where an entry's reopenDate points to
                // the next closed day instead of the actual business reopen date,
                // and edge cases where the nominal reopenDate falls on a Sunday.
                const effectiveReopenDate = new Date(reopenDate);
                while (
                    filteredClosedDates.includes(
                        effectiveReopenDate.toLocaleDateString(LOCALE_DATE_FORMAT)
                    ) || effectiveReopenDate.getDay() === 0
                ) {
                    effectiveReopenDate.setDate(effectiveReopenDate.getDate() + 1);
                    effectiveReopenDate.setHours(8, 0, 0, 0);
                }

                const dayBeforeReopen = new Date(effectiveReopenDate);
                dayBeforeReopen.setDate(dayBeforeReopen.getDate() - 1);
                dayBeforeReopen.setHours(17, 30, 0, 0);
                // Client ruling (PCH-124 suite Tests 5 & 6, 09/07/2026): the charge
                // point is always 17:30 on the night before reopen — never bumped
                // past the 24-hour mark. A span of ≤ 24h from collection charges
                // 1 day via Math.ceil(daysBetween); only spans over 24h charge 2.

                // Client ruling (call, 11/07/2026): a 24-hour (1-day) rental shows
                // no toaster, closed return day or not — the informational toast
                // only explains charges that extend beyond one day.
                const isOneDayCharge =
                    dayBeforeReopen.getTime() - collectionDate.getTime() <=
                    24 * 60 * 60 * 1000;

                // closingDay is entry.date - 1 day, which is wrong when that day is
                // itself fully closed (e.g. entry = 01/01, closingDay = 31/12, but 31/12
                // is also closed). Walk backwards until we reach the last open day.
                // Walk backwards from closingDay (entry.date - 1) through any CMS-closed
                // days and programmatic Sundays to find the true last open business day.
                const toastClosingDay = new Date(closingDay);
                while (
                    filteredClosedDates.includes(
                        toastClosingDay.toLocaleDateString(LOCALE_DATE_FORMAT)
                    ) || toastClosingDay.getDay() === 0
                ) {
                    toastClosingDay.setDate(toastClosingDay.getDate() - 1);
                }
                const toasterMessage =
                    closedDetail.closingTime !== 'closed'
                        ? `We are closing at ${closedDetail.closingTime} on ${earlyCloseDate.toLocaleDateString(LOCALE_DATE_FORMAT)}`
                        : `We are closing at 6pm on ${toastClosingDay.toLocaleDateString(LOCALE_DATE_FORMAT)}`;

                const isLikelyDefaultMidnight =
                    retDateNormalized.hours === 0 &&
                    retDateNormalized.minutes === 0;

                // === case: charge is already at the correct cutoff — no date correction
                // needed. Fire the informational toast so the customer knows the depot is
                // closed on their selected return date.
                if (retDate.getTime() === dayBeforeReopen.getTime()) {
                    if (!isLikelyDefaultMidnight && !isOneDayCharge) {
                        toastify.info(
                            `We are closed or closing on the day of return, you are welcome to drop the keys but the rental is calculated to ${dayBeforeReopen.toLocaleDateString(LOCALE_DATE_FORMAT)} at 5:30pm (which is the night before we re-open). ${toasterMessage}, any vehicles would need to be returned at least an hour before we close to avoid charging until we re-open ${closedDetail.reopenDate ? `on ${effectiveReopenDate.toLocaleDateString(LOCALE_DATE_FORMAT)}.` : '.'}`,
                            { toastId: 'returningSunday' }
                        );
                    }
                    return;
                }
                // > case: past the charge cutoff — natural 24h cycle is already correct.
                if (retDate.getTime() > dayBeforeReopen.getTime()) {
                    continue;
                }

                setReturnDatePriceCalculator(dayBeforeReopen);
                mySetFieldValue('returnDatePriceCalculator', dayBeforeReopen);

                if (!isLikelyDefaultMidnight && !isOneDayCharge) {
                    toastify.info(
                        `We are closed or closing on the day of return, you are welcome to drop the keys but the rental is calculated to ${dayBeforeReopen.toLocaleDateString(LOCALE_DATE_FORMAT)} at 5:30pm (which is the night before we re-open). ${toasterMessage}, any vehicles would need to be returned at least an hour before we close to avoid charging until we re-open ${closedDetail.reopenDate ? `on ${effectiveReopenDate.toLocaleDateString(LOCALE_DATE_FORMAT)}.` : '.'}`,
                        { toastId: 'returningSunday' }
                    );
                }
                return;
            }
            // If return is on the early close day, but before the 1-hour buffer, fall through to vehicle-specific rules
            if (
                retDate.toDateString() === earlyCloseDate.toDateString() &&
                (hours < closeHour - 1 ||
                    (hours === closeHour - 1 && minutes === 0))
            ) {
                // Do nothing, let vehicle-specific rules apply
            }
        }
    }

    const collectReturnSaturday =
        returnDay === DAYS_OF_WEEK[6] && collectionDay === DAYS_OF_WEEK[6];

    // Enhanced early close day logic - handle this FIRST before any other logic
    if (currentEarlyCloseDate) {
        const handled = handleEarlyCloseDay(
            currentEarlyCloseDate,
            retDate,
            hours,
            minutes,
            returnDatePriceCalculator,
            setReturnDatePriceCalculator,
            mySetFieldValue,
            toastify,
            numRentalDays
        );

        if (handled) return;
    }

    // Handle completely closed days
    // if (closurePeriodInfo.isCompletelyClosedDay) {
    //     const handled = handleCompletelyClosedDay(
    //         retDate,
    //         closedDatesDetails,
    //         returnDatePriceCalculator,
    //         setReturnDatePriceCalculator,
    //         mySetFieldValue,
    //         toastify,
    //         numRentalDays,
    //         collectReturnSaturday,
    //         false
    //     );

    //     if (handled) return;
    // }

    // Handle consecutive closure periods
    const handled = handleConsecutiveClosurePeriod(
        closurePeriodInfo,
        currentEarlyCloseDate,
        retDate,
        sanitizedClosedDatesDetails,
        returnDatePriceCalculator,
        setReturnDatePriceCalculator,
        mySetFieldValue,
        toastify,
        numRentalDays,
        collectReturnSaturday,
        false
    );

    if (handled) return;

    // Now lets check and see if tomorrow is a bank holiday
    const theNextDay = new Date(returnDatePriceCalculator);
    theNextDay.setDate(theNextDay.getDate() + 1);

    const tomorrowDateString =
        theNextDay.toLocaleDateString(LOCALE_DATE_FORMAT);

    // Now lets check and see if tomorrow is a bank holiday
    const theSecondDay = new Date(returnDatePriceCalculator);
    theSecondDay.setDate(theSecondDay.getDate() + 2);

    const returnDateString = formatForComparison(retDate);

    const isClosed = normalizedClosedDates.includes(returnDateString);

    const isClosedDayTomorrow =
        normalizedClosedDates.includes(tomorrowDateString);

    const retDateString = formatForComparison(retDate);

    const isVan = vehicle?.type === VEHICLE_TYPES.VAN;
    const isCar = vehicle?.type === VEHICLE_TYPES.CAR;

    const isOneDayRental = numRentalDays === 1;

    const collectHours = colDate.getHours();
    const collectMinutes = colDate.getMinutes();

    const isAfter5pmCollection =
        collectHours > TIME_CONSTANTS.HOURS_5PM ||
        (collectHours === TIME_CONSTANTS.HOURS_5PM &&
            collectMinutes > TIME_CONSTANTS.MINUTES_0);

    const isAfter5pm =
        hours > TIME_CONSTANTS.HOURS_5PM ||
        (hours === TIME_CONSTANTS.HOURS_5PM &&
            minutes > TIME_CONSTANTS.MINUTES_0);
    const isAfter6pm =
        hours > TIME_CONSTANTS.HOURS_6PM ||
        (hours === TIME_CONSTANTS.HOURS_6PM &&
            minutes > TIME_CONSTANTS.MINUTES_0);
    const isAfter12pm =
        hours > TIME_CONSTANTS.HOURS_12PM ||
        (hours === TIME_CONSTANTS.HOURS_12PM &&
            minutes > TIME_CONSTANTS.MINUTES_0);
    const isAfter9am =
        hours > TIME_CONSTANTS.HOURS_9AM ||
        (hours === TIME_CONSTANTS.HOURS_9AM &&
            minutes > TIME_CONSTANTS.MINUTES_0);
    const isBefore9am = hours < TIME_CONSTANTS.HOURS_9AM;
    const isExactly9amCollection =
        collectHours === TIME_CONSTANTS.HOURS_9AM &&
        collectMinutes === TIME_CONSTANTS.MINUTES_0;
    const isExactly9amReturn =
        hours === TIME_CONSTANTS.HOURS_9AM &&
        minutes === TIME_CONSTANTS.MINUTES_0;
    const isExactly5pmReturn =
        hours === TIME_CONSTANTS.HOURS_5PM &&
        minutes === TIME_CONSTANTS.MINUTES_0;
    const isCollectedBefore530pmSaturday =
        collectionDay === DAYS_OF_WEEK[6] &&
        (collectHours < TIME_CONSTANTS.HOURS_5PM ||
            (collectHours === TIME_CONSTANTS.HOURS_5PM &&
                collectMinutes < TIME_CONSTANTS.MINUTES_30));

    // Check for Friday to Saturday overnight rental
    const isFridayToSaturday =
        (collectionDay === DAYS_OF_WEEK[5] &&
            returnDay === DAYS_OF_WEEK[6] &&
            isOneDayRental) ||
        daysBetween(colDate, retDate) <= 1;

    const isEarlyCloseDay = sanitizedClosedDatesDetails.some(
        (d) => d.date === retBusinessDate && d.closingTime !== 'closed'
    );

    // Only run closedDatesCalculator if this isn't an early close day with early return
    let consecutiveDays = 0;
    let nextOpenDay = null;
    const earlyCloseDetails = sanitizedClosedDatesDetails.find(
        (d) => d.date === retBusinessDate
    );
    const isEarlyReturnOnEarlyCloseDay =
        earlyCloseDetails &&
        earlyCloseDetails.closingTime !== 'closed' &&
        hours < 12; // Returning strictly before noon — hours=12 at 12:30pm must not skip closedDatesCalculator

    if (!isEarlyReturnOnEarlyCloseDay) {
        const result = closedDatesCalculator(
            filteredClosedDates,
            retBusinessDate
        );
        consecutiveDays = result.consecutiveDays;
        nextOpenDay = result.nextOpenDay;
    }

    // Define a helper function to determine if we need to add an extra day
    const needsExtraDayForSaturdayReturn = (
        isAfter5pm: boolean,
        returnHours: number,
        returnMinutes: number,
        collectHours: number,
        collectMinutes: number
    ) => {
        // Convert hours and minutes to comparable values
        const returnTime = returnHours * 60 + returnMinutes;
        const collectTime = collectHours * 60 + collectMinutes;

        // Return true if after 5pm and return time is earlier than or equal to collection time
        // OR if after 5pm and collection time is before 5pm
        return (
            isAfter5pm &&
            (returnTime <= collectTime ||
                collectHours < 17 ||
                (collectHours === 17 && collectMinutes === 0))
        );
    };

    // Determine if we need to add 1 or 2 days based on the legacy logic
    const extraDays = needsExtraDayForSaturdayReturn(
        isAfter5pm,
        hours,
        minutes,
        collectHours,
        collectMinutes
    )
        ? 2
        : 1;

    // Close any open toast.
    toast.dismiss();

    // Special case: Friday 9am to Saturday 9am — must be genuinely Fri→Sat,
    // not any 1-day rental (isFridayToSaturday is true for daysBetween <= 1 broadly)
    if (
        isFridayToSaturday &&
        collectionDay === DAYS_OF_WEEK[5] &&
        returnDay === DAYS_OF_WEEK[6] &&
        isCar &&
        isExactly9amCollection &&
        isExactly9amReturn
    ) {
        // This is a one-day rental, no adjustments needed
        return;
    }

    if (isEarlyCloseDay && returnDay !== DAYS_OF_WEEK[6]) {
        if (isAfter12pm) {
            const reopenDisplay =
                nextOpenDay ??
                (earlyCloseDetails?.reopenDate
                    ? new Date(earlyCloseDetails.reopenDate).toLocaleDateString(
                          LOCALE_DATE_FORMAT
                      )
                    : null);
            dateHelper.fixDateAndTime({
                returnDatePriceCalculator,
                setReturnDatePriceCalculator,
                mySetFieldValue,
                toastify,
                numDays: consecutiveDays,
                numRentalDays,
                message: `We are closed on the day of return, you are welcome to drop the keys but the rental is calculated to 5:30pm on the evening before we re-open. We re-open on ${reopenDisplay}.`,
            });

            return;
        }
    }

    if (returnDay === DAYS_OF_WEEK[6]) {
        // return out immediately if we don't have a selected vehicle yet so we don't display any toasters on first load when times are defaulted to
        if (!vehicle?.value) return;

        if (isEarlyCloseDay) {
            if (isAfter12pm) {
                const reopenDisplay =
                    nextOpenDay ??
                    (earlyCloseDetails?.reopenDate
                        ? new Date(
                              earlyCloseDetails.reopenDate
                          ).toLocaleDateString(LOCALE_DATE_FORMAT)
                        : null);
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: consecutiveDays,
                    numRentalDays,
                    message: `We are closed on the day of return, you are welcome to drop the keys but the rental is calculated to 5:30pm on the evening before we re-open. We re-open on ${reopenDisplay}.`,
                });
                // return;
            }
        }

        switch (true) {
            // Special case: Friday evening to early Saturday returns (before 9am)
            case isFridayToSaturday &&
                isCar &&
                isAfter5pmCollection &&
                !isAfter9am:
                return;

            // Handle Friday evening collections returned Saturday 9am-5pm
            case isFridayToSaturday &&
                isCar &&
                isAfter5pmCollection &&
                !isAfter5pm:
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: 2, // Makes it a 2-day rental
                    numRentalDays,
                    collectReturnSaturday,
                    message:
                        'There is a minimum charge of 2 days if a car is to be returned between Saturday 9am and Sunday 5pm.',
                });
                break;

            // Handle Friday evening collections returned Saturday by 5pm, or Saturday only as a single day rental
            case ((isFridayToSaturday &&
                isExactly5pmReturn &&
                isAfter5pmCollection) ||
                (isCollectedBefore530pmSaturday &&
                    colDate.getDay() === retDate.getDay() &&
                    !isAfter5pm)) &&
                isVan:
                return;

            // Handle Friday evening collections returned Saturday after 5pm
            case (isFridayToSaturday &&
                isCar &&
                isAfter5pmCollection &&
                isAfter5pm) ||
                (isCollectedBefore530pmSaturday &&
                    colDate.getDay() === retDate.getDay() &&
                    colDate.getDate() === retDate.getDate() &&
                    isAfter5pm &&
                    isVan):
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: isVan ? 1 : extraDays,
                    numRentalDays,
                    collectReturnSaturday,
                    message:
                        'If returning after 5pm on a Saturday the rental is charged to Sunday 5pm.',
                });
                break;
            case isOneDayRental &&
                isCar &&
                !isBefore9am &&
                !isAfter5pm &&
                !(isFridayToSaturday && isExactly9amReturn):
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: isOneDayRental ? 1 : isAfter6pm ? 1 : 0,
                    numRentalDays,
                    collectReturnSaturday,
                    message:
                        'There is a minimum charge of 2 days if a car is to be returned between Saturday 9am and Sunday 5pm.',
                });
                dateAlreadyProcessed = true;
                break;
            case isAfter9am &&
                !isAfter5pm &&
                isOneDayRental &&
                isVan &&
                !(isFridayToSaturday && isAfter5pmCollection):
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: 0,
                    message:
                        'Single Day Van hires being returned on a Saturday after 9am, will be charged until 5pm Saturday. For a single day hire on a Saturday you can collect from 5:30pm on a Friday and return by 5pm on Saturday.',
                });
                break;
            case isAfter5pm && isOneDayRental && isVan:
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: 1,
                    message:
                        'Single Day Van hires being returned on a Saturday after 9am, will be charged until 5pm Saturday. For a single day hire on a Saturday you can collect from 5:30pm on a Friday and return by 5pm on Saturday.',
                });
                break;
            case isAfter12pm && isClosed && !isEarlyCloseDay:
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: 0,
                    message:
                        'We are closed on the day of return, you are welcome to drop the keys but the rental is calculated to 5pm on the evening before we re-open.',
                });
                break;
            case (isAfter5pm && isOneDayRental && isCar) ||
                (isAfter5pm && !isOneDayRental && isCar) ||
                isAfter5pm:
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: 1,
                    message:
                        'If returning after 5pm on a Saturday the rental is charged to Sunday 5pm.',
                });
                break;
            default:
                // Handle the default case if needed.
                break;
        }
    } else if (returnDay === DAYS_OF_WEEK[0]) {
        // return out immediately if we don't have a selected vehicle yet so we don't display any toasters on first load when times are defaulted to
        if (!vehicle?.value) return;
        switch (true) {
            // Handle Friday to Sunday van rentals returned after 6pm: silently charge to
            // Monday 5:30pm. Earlier Sunday returns fall through to the standard Sunday
            // rule (charged to Sunday 5:30pm with a toaster), the same as cars (PCH-131).
            case isVan && collectionDay === DAYS_OF_WEEK[5] && isAfter6pm: {
                // Create a new date object for Monday at 5:30pm
                const newReturnDate = new Date(returnDatePriceCalculator);
                newReturnDate.setDate(newReturnDate.getDate() + 1);
                newReturnDate.setHours(17, 30, 0, 0);

                // Update the return date without showing a toast
                setReturnDatePriceCalculator(newReturnDate);
                mySetFieldValue('returnDatePriceCalculator', newReturnDate);
                return;
            }

            case (isOneDayRental || daysBetween(colDate, retDate) < 1) &&
                isVan &&
                collectionDay === DAYS_OF_WEEK[6] &&
                collectHours === TIME_CONSTANTS.HOURS_5PM &&
                collectMinutes === TIME_CONSTANTS.MINUTES_0:
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: 0,
                    message:
                        'If you need a Van for a 1 Day hire on a Sunday, it can only be collected 5:30pm on Saturday and returned by 5:30pm on Sunday, otherwise, 2 days are charged.',
                });
                break;

            case isOneDayRental &&
                isVan &&
                returnDay === DAYS_OF_WEEK[0] &&
                isCollectedBefore530pmSaturday &&
                !isAfter5pm:
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: 0,
                    message:
                        'If returning on a Sunday rental is charged to 5pm.',
                });
                break;
            case isOneDayRental && isCar:
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: isAfter5pmCollection ? 1 : 0,
                    message:
                        'If returning a car on a Sunday, the minimum charge is 2 days.',
                });
                break;
            case !isOneDayRental && isAfter9am && isCar:
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: 0,
                    message:
                        'If returning after 5pm on a Saturday the rental is charged to Sunday at 5:30pm.',
                });
                break;
            case isClosedDayTomorrow:
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: 1,
                    message:
                        'We are closed on the day of return, you are welcome to drop the keys but the rental is calculated to 5:30pm on the evening before we re-open.',
                });
                break;
            case (daysBetween(colDate, retDate) <= 1 &&
                isAfter5pmCollection &&
                isVan) ||
                (!isOneDayRental &&
                    collectionDay === DAYS_OF_WEEK[6] &&
                    colDateNormalized.hours < TIME_CONSTANTS.HOURS_5PM &&
                    retDateNormalized.hours <= TIME_CONSTANTS.HOURS_5PM):
                // Standard single day rental, no adjustments or toasters needed
                return;
            default:
                dateHelper.fixDateAndTime({
                    returnDatePriceCalculator,
                    setReturnDatePriceCalculator,
                    mySetFieldValue,
                    toastify,
                    numDays: 0,
                    message:
                        'If returning after 5pm on a Saturday the rental is charged to Sunday at 5:30pm.',
                });
                break;
        }
    } else if (
        isClosed &&
        !isAfter5pm &&
        !isEarlyCloseDay &&
        !consecutiveDays
    ) {
        dateHelper.fixDateAndTime({
            returnDatePriceCalculator,
            setReturnDatePriceCalculator,
            mySetFieldValue,
            toastify,
            numDays: 0,
            message: `We are closed on the day of return, you are welcome to drop the keys but the rental is calculated to 5:30pm on the evening before we re-open. We re-open on ${nextOpenDay}.`,
        });
    }

    if (consecutiveDays > 0 && !dateAlreadyProcessed) {
        // TODO: SSC - possibly bring back for post 07/11 version
        // const retTime = new Date(
        //     returnDatePriceCalculator as unknown as Date
        // ).getHours();
        // const colTime = new Date(collectionDate as unknown as Date).getHours();
        // const additionalDay = retTime === colTime ? 1 : 0;
        // If this is an early close day and we're returning well before close time, skip consecutive logic
        const earlyCloseDetails = sanitizedClosedDatesDetails.find(
            (d) => d.date === retDateString
        );

        if (earlyCloseDetails && earlyCloseDetails.closingTime !== 'closed') {
            const [closeHour] = earlyCloseDetails.closingTime
                .split(':')
                .map(Number);
            const closeTimeInMinutes = closeHour * 60;
            const returnTimeInMinutes = hours * 60 + minutes;
            const twoHoursBeforeClose = closeTimeInMinutes - 120;

            if (returnTimeInMinutes < twoHoursBeforeClose) {
                return;
            }
        }
        dateHelper.fixDateAndTime({
            returnDatePriceCalculator,
            setReturnDatePriceCalculator,
            mySetFieldValue,
            toastify,
            numDays: consecutiveDays,
            message: `We are closed on the day of return, you are welcome to drop the keys but the rental is calculated to 5:30pm on the evening before we re-open. We re-open on ${nextOpenDay}.`,
        });
    }
};

export default returnDateCalculator;
